import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useInvalidateAutomationGuides } from "@/hooks/useAutomationGuides";
import { useInvalidateExistingAutomationGuides } from "@/hooks/useExistingAutomationGuides";
import { useInvalidateDashboard } from "@/hooks/useDashboardData";
import {
  usePendingGuidesPersistence,
  type PendingGuide,
  type PendingDirectGuide,
  type StorageResult,
} from "@/hooks/usePendingGuidesPersistence";
import { useGuideTimeout } from "@/hooks/useGuideTimeout";
import {
  usePendingGuidesRealtime,
  useFallbackCheck,
  useDirectFallbackCheck,
} from "@/hooks/usePendingGuidesRealtime";
import { translateToast } from "@/lib/i18n-toast";

interface PendingGuidesContextType {
  pendingGuides: Map<string, PendingGuide>;
  pendingDirectGuides: Map<string, PendingDirectGuide>;
  startGuideGeneration: (
    sopId: string,
    sopTitle: string,
    jobId?: string,
  ) => void;
  startDirectGuideGeneration: (processName: string, jobId: string) => void;
  updateJobId: (sopId: string, jobId: string) => void;
  cancelGuideGeneration: (sopId: string) => void;
  cancelDirectGuideGeneration: (jobId: string) => void;
  isGenerating: (sopId: string) => boolean;
  isDirectGuideGenerating: (jobId: string) => boolean;
  hasAnyPending: boolean;
  setIsPreparingGuide: (preparing: boolean) => void;
}

const PendingGuidesContext = createContext<PendingGuidesContextType | null>(
  null,
);

export function PendingGuidesProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const invalidateGuides = useInvalidateAutomationGuides();
  const invalidateExistingGuides = useInvalidateExistingAutomationGuides();
  const invalidateDashboard = useInvalidateDashboard();

  // Persistence hooks
  const {
    pendingGuides,
    pendingDirectGuides,
    addPendingGuide,
    deletePendingGuide,
    updatePendingGuideJobId,
    addPendingDirectGuide,
    deletePendingDirectGuide,
    isGenerating,
    isDirectGuideGenerating,
    hasAnyPending,
    setIsPreparingGuide,
    lastStorageError,
    clearStorageError,
  } = usePendingGuidesPersistence();

  // Show toast notification when localStorage error occurs
  useEffect(() => {
    if (lastStorageError && !lastStorageError.success) {
      toast({
        title: translateToast("pendingGuides.storageWarning"),
        description: lastStorageError.errorMessage,
        variant: "destructive",
      });
      clearStorageError();
    }
  }, [lastStorageError, clearStorageError, toast, t]);

  // Refs to track current pending guides for use in callbacks (avoids stale closure)
  const pendingGuidesRef = useRef<Map<string, PendingGuide>>(pendingGuides);
  const pendingDirectGuidesRef =
    useRef<Map<string, PendingDirectGuide>>(pendingDirectGuides);

  // Track if we've initialized restored jobs
  const initializedRef = useRef(false);
  const directInitializedRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    pendingGuidesRef.current = pendingGuides;
  }, [pendingGuides]);

  useEffect(() => {
    pendingDirectGuidesRef.current = pendingDirectGuides;
  }, [pendingDirectGuides]);

  // Handle timeout for guides
  const handleTimeout = useCallback(
    (sopId: string, sopTitle: string) => {
      deletePendingGuide(sopId);
      toast({
        title: translateToast("guides.generationFailed"),
        description: translateToast("pendingGuides.generationTimedOutDesc", {
          title: sopTitle,
        }),
        variant: "destructive",
      });
    },
    [deletePendingGuide, toast, t],
  );

  // Handle timeout for direct guides
  const handleDirectTimeout = useCallback(
    (jobId: string, processName: string) => {
      deletePendingDirectGuide(jobId);
      toast({
        title: translateToast("guides.generationFailed"),
        description: translateToast("pendingGuides.generationTimedOutDesc", {
          title: processName,
        }),
        variant: "destructive",
      });
    },
    [deletePendingDirectGuide, toast, t],
  );

  // Handle guide completed
  const handleGuideCompleted = useCallback(
    (sopId: string, _guideId: string, _sopTitle: string) => {
      deletePendingGuide(sopId);
    },
    [deletePendingGuide],
  );

  // Handle direct guide completed
  const handleDirectGuideCompleted = useCallback(
    (jobId: string, _guideId: string, _processName: string) => {
      deletePendingDirectGuide(jobId);
    },
    [deletePendingDirectGuide],
  );

  // Handle guide failed
  const handleGuideFailed = useCallback(
    (sopId: string, _error?: string, _sopTitle?: string) => {
      deletePendingGuide(sopId);
    },
    [deletePendingGuide],
  );

  // Handle direct guide failed
  const handleDirectGuideFailed = useCallback(
    (jobId: string, _error?: string, _processName?: string) => {
      deletePendingDirectGuide(jobId);
    },
    [deletePendingDirectGuide],
  );

  // Fallback check hook (for SOP-based guides)
  const { performFallbackCheck } = useFallbackCheck(
    pendingGuidesRef,
    handleGuideCompleted,
    handleGuideFailed,
  );

  // Direct fallback check hook (for direct guides without SOP)
  // Handles mobile browser backgrounding, screen lock, etc.
  const { performDirectFallbackCheck } = useDirectFallbackCheck(
    pendingDirectGuidesRef,
    handleDirectGuideCompleted,
    handleDirectGuideFailed,
    invalidateGuides,
    invalidateExistingGuides,
    invalidateDashboard,
  );

  // Timeout management hook
  const {
    startTimeout,
    startDirectTimeout,
    clearGuideTimeout,
    clearDirectGuideTimeout,
    initializeRestoredGuides,
    initializeRestoredDirectGuides,
  } = useGuideTimeout({
    onTimeout: handleTimeout,
    onDirectTimeout: handleDirectTimeout,
    onFallbackCheck: performFallbackCheck,
    onDirectFallbackCheck: performDirectFallbackCheck,
  });

  // Realtime subscriptions
  usePendingGuidesRealtime({
    enabled: hasAnyPending,
    pendingGuidesRef,
    pendingDirectGuidesRef,
    onGuideCompleted: (sopId, guideId, sopTitle) => {
      clearGuideTimeout(sopId);
      handleGuideCompleted(sopId, guideId, sopTitle);
    },
    onDirectGuideCompleted: (jobId, guideId, processName) => {
      clearDirectGuideTimeout(jobId);
      handleDirectGuideCompleted(jobId, guideId, processName);
    },
    onGuideFailed: (sopId, error, sopTitle) => {
      clearGuideTimeout(sopId);
      handleGuideFailed(sopId, error, sopTitle);
    },
    onDirectGuideFailed: (jobId, error, processName) => {
      clearDirectGuideTimeout(jobId);
      handleDirectGuideFailed(jobId, error, processName);
    },
  });

  // Initialize restored jobs from localStorage on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    initializeRestoredGuides(pendingGuides);
  }, [pendingGuides, initializeRestoredGuides]);

  // Initialize restored direct jobs from localStorage on mount
  useEffect(() => {
    if (directInitializedRef.current) return;
    directInitializedRef.current = true;

    initializeRestoredDirectGuides(pendingDirectGuides);
  }, [pendingDirectGuides, initializeRestoredDirectGuides]);

  // Visibility change listener - check pending guides when app returns to foreground
  // This handles mobile browsers that suspend WebSocket connections when backgrounded
  useEffect(() => {
    const VISIBILITY_CHECK_DEBOUNCE_MS = 500;
    const VISIBILITY_CHECK_COOLDOWN_MS = 5000;
    const visibilityTimeoutRef: { current: number | null } = { current: null };
    const lastVisibilityCheckAtRef: { current: number } = { current: 0 };

    const runChecks = () => {
      const now = Date.now();
      if (
        now - lastVisibilityCheckAtRef.current <
        VISIBILITY_CHECK_COOLDOWN_MS
      ) {
        return;
      }
      lastVisibilityCheckAtRef.current = now;

      // Check all pending direct guides when app becomes visible
      pendingDirectGuidesRef.current.forEach((guide) => {
        performDirectFallbackCheck(guide.jobId);
      });

      // Check all pending SOP guides when app becomes visible
      pendingGuidesRef.current.forEach((guide) => {
        if (guide.jobId) {
          performFallbackCheck(guide.jobId, guide.sopId);
        }
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (
          pendingDirectGuidesRef.current.size === 0 &&
          pendingGuidesRef.current.size === 0
        ) {
          return;
        }

        // Debounce and add a cooldown so rapid tab switching doesn't trigger
        // dozens of fallback queries in a few seconds.
        if (visibilityTimeoutRef.current !== null) {
          clearTimeout(visibilityTimeoutRef.current);
        }
        visibilityTimeoutRef.current = window.setTimeout(() => {
          visibilityTimeoutRef.current = null;
          if (document.visibilityState !== "visible") return;
          runChecks();
        }, VISIBILITY_CHECK_DEBOUNCE_MS);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (visibilityTimeoutRef.current !== null) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [performDirectFallbackCheck, performFallbackCheck]);

  // Start guide generation
  const startGuideGeneration = useCallback(
    (sopId: string, sopTitle: string, jobId?: string) => {
      const now = Date.now();
      addPendingGuide({ sopId, sopTitle, startedAt: now, jobId });
      startTimeout(sopId, sopTitle, now, jobId);
    },
    [addPendingGuide, startTimeout],
  );

  // Start direct guide generation
  const startDirectGuideGeneration = useCallback(
    (processName: string, jobId: string) => {
      const now = Date.now();
      addPendingDirectGuide({ jobId, processName, startedAt: now });
      startDirectTimeout(jobId, processName, now);
    },
    [addPendingDirectGuide, startDirectTimeout],
  );

  // Update job ID
  const updateJobId = useCallback(
    (sopId: string, jobId: string) => {
      updatePendingGuideJobId(sopId, jobId);
    },
    [updatePendingGuideJobId],
  );

  // Cancel guide generation
  const cancelGuideGeneration = useCallback(
    (sopId: string) => {
      clearGuideTimeout(sopId);
      deletePendingGuide(sopId);
    },
    [clearGuideTimeout, deletePendingGuide],
  );

  // Cancel direct guide generation
  const cancelDirectGuideGeneration = useCallback(
    (jobId: string) => {
      clearDirectGuideTimeout(jobId);
      deletePendingDirectGuide(jobId);
    },
    [clearDirectGuideTimeout, deletePendingDirectGuide],
  );

  const contextValue = useMemo(
    () => ({
      pendingGuides,
      pendingDirectGuides,
      startGuideGeneration,
      startDirectGuideGeneration,
      updateJobId,
      cancelGuideGeneration,
      cancelDirectGuideGeneration,
      isGenerating,
      isDirectGuideGenerating,
      hasAnyPending,
      setIsPreparingGuide,
    }),
    [
      pendingGuides,
      pendingDirectGuides,
      startGuideGeneration,
      startDirectGuideGeneration,
      updateJobId,
      cancelGuideGeneration,
      cancelDirectGuideGeneration,
      isGenerating,
      isDirectGuideGenerating,
      hasAnyPending,
      setIsPreparingGuide,
    ],
  );

  return (
    <PendingGuidesContext.Provider value={contextValue}>
      {children}
    </PendingGuidesContext.Provider>
  );
}

export function usePendingGuides() {
  const context = useContext(PendingGuidesContext);
  if (!context) {
    throw new Error(
      "usePendingGuides must be used within a PendingGuidesProvider",
    );
  }
  return context;
}
